import Grid from '@mui/material/Grid';
import React, { useEffect, useState, useMemo } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import W86AutoComplete from '../Component/W86AutoComplete';
import { send_request } from '../../utils/Request';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { ComponentFactory, isComponentTierUnlocked } from '../ProjectCanvas/ComponentRegistry';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import checkKey from '../../utils/CheckKey';
import PipedreamAppSearchPicker from './PipedreamAppSearchPicker';
import PipedreamInlineConfigForm from './PipedreamInlineConfigForm';
import useConnectedAppOptions from './useConnectedAppOptions';
import CustomPaper from '../common/CustomPaper';
import UpgradeBanner from '../TriggerFromExternal/UpgradeBanner';
import '../TriggerFromExternal/TriggerFromExternalTemplate.css';

const LoadingContainer = styled(Grid)(() => ({
  minHeight: 300,
}));

const LoadingItem = styled(Grid)(() => ({
  width: '100%',
}));

const LoadingSkeleton = styled(Skeleton)(() => ({
  borderRadius: '8px',
  width: '100%',
  height: 300,
}));

/**
 * Shared full-pane editor for the Pipedream-backed external-app components. Behaviour is identical for
 * triggers and actions; everything that differs comes from `capabilityConfig` (externalAppCapability.js).
 * The dynamic-props form is delegated to PipedreamInlineConfigForm (same component the inline node uses),
 * which owns the Connect client, loading skeleton, and first-update guard.
 */
const ExternalAppTemplate = (props) => {
  const {
    componentType,
    capability,
    itemLabel,
    itemPlaceholder,
    outputPrefix,
    getStore,
    fetchItems,
    selectedKeyField,
  } = props.capabilityConfig;

  const params = useParams();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  const [configuredProps, setConfiguredProps] = useState({});
  const [selectedApp, setSelectedApp] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState({});
  const [outputPlaceholderKey, setOutputPlaceholderKey] = useState('');
  const [outputPlaceholderError, setOutputPlaceholderError] = useState('');
  const [bannerAnimating, setBannerAnimating] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const store = useMemo(() => getStore(props.component_id), [getStore, props.component_id]);

  const defaultAppOptions = useConnectedAppOptions();

  // Tier entitlement comes from the registry (single source of truth), not a hardcoded tier string.
  // Subscribing keeps it live if the user's tier changes.
  const canConfigure = useProjectStore((state) =>
    isComponentTierUnlocked(ComponentFactory[componentType]?.tier, state.tier)
  );

  const animateUpgradeBanner = () => {
    setBannerAnimating(true);
    setTimeout(() => setBannerAnimating(false), 600);
  };

  const outputPlaceholderOnChange = (event) => {
    if (!canConfigure) return;
    const val = replacePlaceholderValue(event.target.value);
    const check = checkKey(val);
    if (Array.isArray(check) && check.length > 1 && check[0] === true) {
      setOutputPlaceholderError(check[1]);
    } else setOutputPlaceholderError('');

    setOutputPlaceholderKey(event.target.value);
  };

  useEffect(() => {
    const projectId = params.id;
    send_request(`authz-service/get_client_by_project/${projectId}`, '', {}, 'GET').then(
      (response) => {
        const retrievedClientId = response?.data;
        if (retrievedClientId) {
          setClientId(retrievedClientId);
        } else {
          console.error('Failed to retrieve client id');
        }
      }
    );
  }, [params]);

  useEffect(() => {
    if (store.getState().loaded) {
      setInitialLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setInitialLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((componentResponse) => {
        const componentData = componentResponse?.data?.components?.[0]?.componentData || {};
        const selectedAppNameSlug = componentData?.selectedAppNameSlug;

        if (selectedAppNameSlug) {
          setSelectedApp({
            nameSlug: selectedAppNameSlug,
            name: componentData?.selectedAppName || selectedAppNameSlug,
            imgSrc: componentData?.selectedAppImgSrc || '',
          });
        }
        setSelectedItem(componentData?.[selectedKeyField] || {});
        setConfiguredProps(componentData?.configuredProps || {});
        setOutputPlaceholderKey(
          componentData?.outputPlaceholderKey || `${outputPrefix}${uuidv4().split('-')[0]}`
        );
      })
      .finally(() => {
        store.setState({ loaded: true });
        props.setChildTemplateLoaded(true);
        setInitialLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the app's triggers/actions when the app changes and resolve a saved key to the full object.
  useEffect(() => {
    if (!selectedApp) {
      return;
    }
    setItemsLoading(true);
    fetchItems(selectedApp.nameSlug)
      .then((items) => {
        setItemOptions(items);
        if (typeof selectedItem === 'string') {
          setSelectedItem(items.find((item) => item.key === selectedItem) || {});
        }
      })
      .catch(() => {})
      .finally(() => setItemsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp]);

  return (
    <CustomPaper className="main-content">
      {initialLoading ? (
        <LoadingContainer
          container
          direction="column"
          spacing={1}
          justifyContent="center"
          alignItems="center"
        >
          <LoadingItem item xs={12}>
            <LoadingSkeleton variant="rectangular" />
          </LoadingItem>
        </LoadingContainer>
      ) : (
        <>
          {!canConfigure && (
            <UpgradeBanner
              animating={bannerAnimating}
              onUpgradeClick={() => navigate('/organization')}
            />
          )}

          <Grid
            container
            direction="column"
            spacing={1}
            className={!canConfigure ? 'disabled' : ''}
          >
            <Grid item>
              <PipedreamAppSearchPicker
                capability={capability}
                title={'Select an App'}
                value={selectedApp}
                defaultOptions={defaultAppOptions}
                placeholder={'Search for an app...'}
                onSelect={(newValue) => {
                  if (!canConfigure) {
                    animateUpgradeBanner();
                    return;
                  }
                  setSelectedApp(newValue);
                  setSelectedItem({});
                  if (newValue) {
                    setItemsLoading(true);
                  }
                }}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid item>
              {selectedApp && !itemsLoading && (
                <W86AutoComplete
                  title={itemLabel}
                  value={selectedItem}
                  onChange={(e, newValue) => {
                    if (!canConfigure) return;
                    setSelectedItem(newValue);
                    setConfiguredProps({});
                  }}
                  options={itemOptions}
                  inputPlaceholder={itemPlaceholder}
                  renderOptionLabel={(option) => option?.name || ''}
                  disabled={!selectedApp || !canConfigure}
                  sx={{ mb: 1 }}
                />
              )}
              {itemsLoading && (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mb: 1, borderRadius: '4px' }}
                />
              )}
            </Grid>
            {selectedItem?.key && canConfigure && (
              <Grid item sx={{ mb: 1 }}>
                <PipedreamInlineConfigForm
                  clientId={clientId}
                  componentKey={selectedItem.key}
                  configurableProps={selectedItem?.configurableProps}
                  configuredProps={configuredProps}
                  onUpdateConfiguredProps={setConfiguredProps}
                />
              </Grid>
            )}

            <Grid item>
              <Typography className="output-placeholder-label">Output Placeholder</Typography>
              <W86PlaceholderTextField
                value={outputPlaceholderKey}
                placeholderText="Enter an output placeholder"
                error={outputPlaceholderError}
                customStyleProps={{ width: '100%' }}
                handleChange={outputPlaceholderOnChange}
                disabled={!canConfigure}
              />
            </Grid>
          </Grid>
        </>
      )}
    </CustomPaper>
  );
};

export default ExternalAppTemplate;
